/**
 * useAudio Composable
 *
 * Manages remote audio playback with per-player mute controls and audio level visualization.
 * This complements useVideo (local media) and useWebRTC (peer connections).
 *
 * Key features:
 * - Per-player mute/unmute controls
 * - Audio level visualization using AudioContext/AnalyserNode
 * - Cross-browser compatibility (Safari webkitAudioContext)
 * - Autoplay policy handling
 */

import { ref, readonly, onUnmounted } from 'vue'
import { Logger, LogCategory } from '@/utils/Logger.class'

// Types (using const objects instead of enums due to erasableSyntaxOnly)
export const AudioPlaybackState = {
  IDLE: 'idle',
  PLAYING: 'playing',
  BLOCKED: 'blocked', // Autoplay blocked by browser
  FAILED: 'failed',
} as const
export type AudioPlaybackState = (typeof AudioPlaybackState)[keyof typeof AudioPlaybackState]

export interface RemoteAudioState {
  playerId: string
  state: AudioPlaybackState
  muted: boolean
  volume: number // 0-1
  audioLevel: number // 0-100 for visualization
}

// Module-level state (shared across all instances)
const remoteAudioStates = ref<Map<string, RemoteAudioState>>(new Map())
const masterMuted = ref(false)
const autoplayBlocked = ref(false)

// Audio elements for each remote player (hidden, for audio playback)
const audioElements = new Map<string, HTMLAudioElement>()

// AudioContext for level monitoring
let audioContext: AudioContext | null = null
const analysers = new Map<string, AnalyserNode>()
const audioSources = new Map<string, MediaStreamAudioSourceNode>() // Store for cleanup
const animationFrameIds = new Map<string, number>()

// Logger instance
function getLogger() {
  return Logger.getInstance()
}

/**
 * Create AudioContext with cross-browser support
 */
function createAudioContext(): AudioContext | null {
  try {
    const AudioContextClass =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    if (AudioContextClass) {
      return new AudioContextClass()
    }
  } catch (e) {
    getLogger().warn(LogCategory.AUDIO, 'AudioContext not supported', e)
  }
  return null
}

/**
 * Ensure AudioContext is ready (resume if suspended)
 */
async function ensureAudioContextReady(): Promise<boolean> {
  if (!audioContext) {
    audioContext = createAudioContext()
  }
  if (audioContext && audioContext.state === 'suspended') {
    try {
      await audioContext.resume()
    } catch (e) {
      getLogger().warn(LogCategory.AUDIO, 'Failed to resume AudioContext', e)
      return false
    }
  }
  return !!audioContext && audioContext.state === 'running'
}

/**
 * Main composable function
 */
export function useAudio() {
  /**
   * Attach remote audio from a MediaStream
   * Creates a hidden audio element and sets up level monitoring
   */
  async function attachRemoteAudio(playerId: string, stream: MediaStream): Promise<void> {
    const audioTracks = stream.getAudioTracks()

    console.log('[AUDIO-RX-1] attachRemoteAudio called', {
      playerId,
      audioTracksCount: audioTracks.length,
      tracks: audioTracks.map(t => ({
        id: t.id,
        enabled: t.enabled,
        readyState: t.readyState,
        muted: t.muted,
      })),
    })

    getLogger().info(LogCategory.AUDIO, 'Attaching remote audio', {
      playerId,
      audioTracks: audioTracks.length,
    })

    // Check if audio tracks exist
    if (audioTracks.length === 0) {
      getLogger().debug(LogCategory.AUDIO, 'No audio tracks in stream', { playerId })
      console.log('[AUDIO-RX-1] No audio tracks in stream, skipping', { playerId })
      return
    }

    // Enhanced validation: check track state, not just count
    const liveTrack = audioTracks.find(t => t.readyState === 'live')
    if (!liveTrack) {
      getLogger().warn(LogCategory.AUDIO, 'No live audio track found', {
        playerId,
        tracks: audioTracks.map(t => ({ readyState: t.readyState, enabled: t.enabled })),
      })
      console.warn('[AUDIO-RX-1] No live audio track found', {
        playerId,
        tracks: audioTracks.map(t => ({ readyState: t.readyState, enabled: t.enabled })),
      })
      return
    }

    // Monitor track state changes for cleanup
    liveTrack.onended = () => {
      getLogger().info(LogCategory.AUDIO, 'Remote audio track ended', { playerId, trackId: liveTrack.id })
      console.log('[AUDIO-RX-1] Remote audio track ended', { playerId })
      detachRemoteAudio(playerId)
    }

    // Also monitor mute state changes (track disabled by remote)
    liveTrack.onmute = () => {
      getLogger().info(LogCategory.AUDIO, 'Remote audio track muted by sender', { playerId })
      console.log('[AUDIO-RX-1] Remote audio track muted by sender', { playerId })
    }
    liveTrack.onunmute = () => {
      getLogger().info(LogCategory.AUDIO, 'Remote audio track unmuted by sender', { playerId })
      console.log('[AUDIO-RX-1] Remote audio track unmuted by sender', { playerId })
    }

    // Create or reuse audio element
    let audioEl = audioElements.get(playerId)
    if (!audioEl) {
      audioEl = document.createElement('audio')
      audioEl.autoplay = true
      audioEl.playsInline = true
      // Hidden but accessible for debugging
      audioEl.style.display = 'none'
      audioEl.id = `remote-audio-${playerId}`
      document.body.appendChild(audioEl)
      audioElements.set(playerId, audioEl)
    }

    // Set stream
    audioEl.srcObject = stream

    // Initialize state
    const existingState = remoteAudioStates.value.get(playerId)
    const state: RemoteAudioState = {
      playerId,
      state: AudioPlaybackState.IDLE,
      muted: existingState?.muted ?? false,
      volume: existingState?.volume ?? 1,
      audioLevel: 0,
    }
    remoteAudioStates.value.set(playerId, state)

    // Apply mute state
    audioEl.muted = state.muted || masterMuted.value
    audioEl.volume = state.volume

    // Try to play
    console.log('[AUDIO-RX-3] Attempting to play audio element', { playerId, muted: audioEl.muted })
    try {
      await audioEl.play()
      state.state = AudioPlaybackState.PLAYING
      autoplayBlocked.value = false
      getLogger().info(LogCategory.AUDIO, 'Remote audio playing', { playerId })
      console.log('[AUDIO-RX-4] Audio playing successfully', { playerId })
    } catch (e: unknown) {
      if (e instanceof Error && e.name === 'NotAllowedError') {
        state.state = AudioPlaybackState.BLOCKED
        autoplayBlocked.value = true
        getLogger().warn(LogCategory.AUDIO, 'Autoplay blocked by browser', { playerId })
        console.warn('[AUDIO-RX-4] Autoplay BLOCKED by browser', { playerId, error: e.message })
      } else {
        state.state = AudioPlaybackState.FAILED
        getLogger().error(LogCategory.AUDIO, 'Failed to play remote audio', { playerId, error: e })
        console.error('[AUDIO-RX-4] Failed to play audio', { playerId, error: e })
      }
    }

    remoteAudioStates.value.set(playerId, state)

    // Set up audio level monitoring
    setupAudioLevelMonitoring(playerId, stream)
  }

  /**
   * Detach remote audio for a player
   */
  function detachRemoteAudio(playerId: string): void {
    getLogger().info(LogCategory.AUDIO, 'Detaching remote audio', { playerId })
    console.log('[AUDIO-RX-CLEANUP] Detaching remote audio', { playerId })

    // Stop level monitoring animation frame FIRST (prevents race condition)
    const frameId = animationFrameIds.get(playerId)
    if (frameId) {
      cancelAnimationFrame(frameId)
      animationFrameIds.delete(playerId)
    }

    // Disconnect analyser node from audio graph
    const analyser = analysers.get(playerId)
    if (analyser) {
      try {
        analyser.disconnect()
      } catch {
        // Ignore - may already be disconnected
      }
      analysers.delete(playerId)
    }

    // Disconnect source node from audio graph (prevents memory leak)
    const source = audioSources.get(playerId)
    if (source) {
      try {
        source.disconnect()
      } catch {
        // Ignore - may already be disconnected
      }
      audioSources.delete(playerId)
    }

    // Remove audio element
    const audioEl = audioElements.get(playerId)
    if (audioEl) {
      audioEl.pause()
      audioEl.srcObject = null
      audioEl.remove()
      audioElements.delete(playerId)
    }

    // Remove state
    remoteAudioStates.value.delete(playerId)
  }

  /**
   * Mute a specific player's audio
   */
  function mutePlayer(playerId: string): void {
    const state = remoteAudioStates.value.get(playerId)
    if (state) {
      state.muted = true
      remoteAudioStates.value.set(playerId, state)

      const audioEl = audioElements.get(playerId)
      if (audioEl) {
        audioEl.muted = true
      }

      getLogger().info(LogCategory.AUDIO, 'Muted player', { playerId })
    }
  }

  /**
   * Unmute a specific player's audio
   */
  function unmutePlayer(playerId: string): void {
    const state = remoteAudioStates.value.get(playerId)
    if (state) {
      state.muted = false
      remoteAudioStates.value.set(playerId, state)

      const audioEl = audioElements.get(playerId)
      if (audioEl) {
        audioEl.muted = masterMuted.value // Respect master mute
      }

      getLogger().info(LogCategory.AUDIO, 'Unmuted player', { playerId })
    }
  }

  /**
   * Toggle mute for a specific player
   */
  function togglePlayerMute(playerId: string): boolean {
    const state = remoteAudioStates.value.get(playerId)
    if (state) {
      if (state.muted) {
        unmutePlayer(playerId)
        return false
      } else {
        mutePlayer(playerId)
        return true
      }
    }
    return false
  }

  /**
   * Set volume for a specific player (0-1)
   */
  function setPlayerVolume(playerId: string, volume: number): void {
    const clampedVolume = Math.max(0, Math.min(1, volume))
    const state = remoteAudioStates.value.get(playerId)
    if (state) {
      state.volume = clampedVolume
      remoteAudioStates.value.set(playerId, state)

      const audioEl = audioElements.get(playerId)
      if (audioEl) {
        audioEl.volume = clampedVolume
      }
    }
  }

  /**
   * Set master mute (mutes all remote audio)
   */
  function setMasterMute(muted: boolean): void {
    masterMuted.value = muted
    audioElements.forEach((audioEl, playerId) => {
      const state = remoteAudioStates.value.get(playerId)
      // Only apply master mute if player is not individually muted
      audioEl.muted = muted || (state?.muted ?? false)
    })
    getLogger().info(LogCategory.AUDIO, 'Master mute set', { muted })
  }

  /**
   * Resume blocked audio (call from user gesture)
   */
  async function resumeBlockedAudio(): Promise<void> {
    getLogger().info(LogCategory.AUDIO, 'Resuming blocked audio')
    console.log('[AUDIO-RX-5] Resuming blocked audio')

    // Resume AudioContext first
    await ensureAudioContextReady()

    // Try to play all blocked audio elements
    const playPromises: Promise<void>[] = []
    remoteAudioStates.value.forEach((state, playerId) => {
      if (state.state === AudioPlaybackState.BLOCKED) {
        const audioEl = audioElements.get(playerId)
        if (audioEl) {
          playPromises.push(
            audioEl
              .play()
              .then(() => {
                state.state = AudioPlaybackState.PLAYING
                remoteAudioStates.value.set(playerId, state)
                console.log('[AUDIO-RX-5] Resumed audio for', playerId)
              })
              .catch((e) => {
                getLogger().error(LogCategory.AUDIO, 'Failed to resume audio', { playerId, error: e })
                console.error('[AUDIO-RX-5] Failed to resume audio for', playerId, e)
              })
          )
        }
      }
    })

    await Promise.all(playPromises)

    // Check if any are still blocked
    let stillBlocked = false
    remoteAudioStates.value.forEach((state) => {
      if (state.state === AudioPlaybackState.BLOCKED) {
        stillBlocked = true
      }
    })
    autoplayBlocked.value = stillBlocked
    console.log('[AUDIO-RX-5] Resume complete, still blocked:', stillBlocked)
  }

  /**
   * Set up auto-resume listeners for browsers that block autoplay
   * Call this in onMounted to handle first user interaction
   * Returns cleanup function to remove listeners
   */
  function setupAutoResumeListeners(): () => void {
    const handleFirstInteraction = async () => {
      if (autoplayBlocked.value) {
        console.log('[AUDIO-RX-5] Auto-resuming blocked audio on user interaction')
        await resumeBlockedAudio()
      }
      // Remove listeners after first use
      cleanup()
    }

    const cleanup = () => {
      document.removeEventListener('click', handleFirstInteraction)
      document.removeEventListener('keydown', handleFirstInteraction)
    }

    document.addEventListener('click', handleFirstInteraction)
    document.addEventListener('keydown', handleFirstInteraction)

    // Return cleanup function for onUnmounted
    return cleanup
  }

  /**
   * Get audio level for a specific player (0-100)
   */
  function getAudioLevel(playerId: string): number {
    return remoteAudioStates.value.get(playerId)?.audioLevel ?? 0
  }

  /**
   * Check if a player has active audio
   */
  function hasRemoteAudio(playerId: string): boolean {
    return remoteAudioStates.value.has(playerId)
  }

  /**
   * Check if a player is muted
   */
  function isPlayerMuted(playerId: string): boolean {
    return remoteAudioStates.value.get(playerId)?.muted ?? false
  }

  /**
   * Set up audio level monitoring using AnalyserNode
   */
  function setupAudioLevelMonitoring(playerId: string, stream: MediaStream): void {
    if (!audioContext) {
      audioContext = createAudioContext()
    }
    if (!audioContext) {
      return
    }

    try {
      // Cleanup existing source if player is reconnecting
      const existingSource = audioSources.get(playerId)
      if (existingSource) {
        try {
          existingSource.disconnect()
        } catch {
          // Ignore - may already be disconnected
        }
        audioSources.delete(playerId)
      }

      // Cleanup existing analyser if player is reconnecting
      const existingAnalyser = analysers.get(playerId)
      if (existingAnalyser) {
        try {
          existingAnalyser.disconnect()
        } catch {
          // Ignore - may already be disconnected
        }
        analysers.delete(playerId)
      }

      // Create source from stream
      const source = audioContext.createMediaStreamSource(stream)
      audioSources.set(playerId, source) // Store for cleanup

      // Create analyser
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 0.8

      // Connect: source -> analyser (no output needed, just analysis)
      source.connect(analyser)

      analysers.set(playerId, analyser)

      // Start monitoring loop
      const dataArray = new Uint8Array(analyser.frequencyBinCount)

      function updateLevel() {
        const analyserNode = analysers.get(playerId)
        if (!analyserNode) return

        analyserNode.getByteFrequencyData(dataArray)

        // Calculate average level
        let sum = 0
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i]
        }
        const average = sum / dataArray.length
        const level = Math.round((average / 255) * 100)

        // Update state
        const state = remoteAudioStates.value.get(playerId)
        if (state) {
          state.audioLevel = level
          // Force reactivity update
          remoteAudioStates.value.set(playerId, { ...state })
        }

        // Continue loop
        const frameId = requestAnimationFrame(updateLevel)
        animationFrameIds.set(playerId, frameId)
      }

      updateLevel()
    } catch (e) {
      getLogger().warn(LogCategory.AUDIO, 'Failed to set up audio level monitoring', { playerId, error: e })
    }
  }

  /**
   * Cleanup function (call on unmount)
   */
  function cleanup(): void {
    // Stop all monitoring
    animationFrameIds.forEach((frameId) => {
      cancelAnimationFrame(frameId)
    })
    animationFrameIds.clear()

    // Don't clear state - it's shared across instances
    // Only clear if explicitly destroying the composable
  }

  /**
   * Full destroy (clears all state)
   */
  function destroy(): void {
    cleanup()

    // Remove all audio elements
    audioElements.forEach((audioEl) => {
      audioEl.pause()
      audioEl.srcObject = null
      audioEl.remove()
    })
    audioElements.clear()

    // Clear analysers
    analysers.clear()

    // Clear state
    remoteAudioStates.value.clear()

    // Close AudioContext
    if (audioContext) {
      audioContext.close()
      audioContext = null
    }
  }

  // Cleanup on unmount
  onUnmounted(() => {
    cleanup()
  })

  /**
   * Reset all state - FOR TESTING ONLY
   * This clears all module-level state to ensure test isolation
   */
  function _resetForTesting(): void {
    // Clear all state
    remoteAudioStates.value.clear()
    masterMuted.value = false
    autoplayBlocked.value = false

    // Stop all animation frames
    animationFrameIds.forEach((frameId) => {
      cancelAnimationFrame(frameId)
    })
    animationFrameIds.clear()

    // Disconnect and clear analysers
    analysers.forEach((analyser) => {
      try {
        analyser.disconnect()
      } catch {
        // Ignore
      }
    })
    analysers.clear()

    // Disconnect and clear sources
    audioSources.forEach((source) => {
      try {
        source.disconnect()
      } catch {
        // Ignore
      }
    })
    audioSources.clear()

    // Remove and clear audio elements
    audioElements.forEach((audioEl) => {
      audioEl.pause()
      audioEl.srcObject = null
      audioEl.remove()
    })
    audioElements.clear()

    // Close AudioContext
    if (audioContext) {
      try {
        audioContext.close()
      } catch {
        // Ignore
      }
      audioContext = null
    }
  }

  return {
    // State (readonly)
    remoteAudioStates: readonly(remoteAudioStates),
    masterMuted: readonly(masterMuted),
    autoplayBlocked: readonly(autoplayBlocked),

    // Methods
    attachRemoteAudio,
    detachRemoteAudio,
    mutePlayer,
    unmutePlayer,
    togglePlayerMute,
    setPlayerVolume,
    setMasterMute,
    resumeBlockedAudio,
    setupAutoResumeListeners,
    getAudioLevel,
    hasRemoteAudio,
    isPlayerMuted,

    // Lifecycle
    cleanup,
    destroy,

    // Testing only
    _resetForTesting,
  }
}
